import { useLayoutEffect, useRef, useState } from 'react'
import { Button, Form, message, Typography, Upload } from 'antd'
import { CheckCircleFilled, InboxOutlined, LoadingOutlined } from '@ant-design/icons'
import type { UploadFile, UploadProps } from 'antd'
import { uploadKycDocument } from '../../services/kyc'

const { Text } = Typography
const { Dragger } = Upload

export interface KycStatusData {
  status?: string
  type?: string
  submittedAt?: number
  reviewedAt?: number
  rejectReason?: string
}

export interface FileUrls {
  legalPersonIdFrontUrl?: string
  legalPersonIdBackUrl?: string
  certificateOfIncorporationUrl?: string
  businessRegistrationUrl?: string
  shareholderStructureUrl?: string
  narOrNcc1Url?: string
  personalDocumentFrontUrl?: string
  personalDocumentBackUrl?: string
  personalHeldIdPhotoUrl?: string
}

interface FileUploadItemProps {
  label: string
  required?: boolean
  fieldKey: keyof FileUrls
  onUploaded: (key: keyof FileUrls, url: string) => void
  uploaded?: string
}

export function FileUploadItem({ label, required = true, fieldKey, onUploaded, uploaded }: FileUploadItemProps) {
  const [uploading, setUploading] = useState(false)
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const active = useRef(true)

  useLayoutEffect(() => {
    active.current = true
    return () => { active.current = false }
  }, [])

  const props: UploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    accept: '.jpg,.jpeg,.png,.pdf',
    beforeUpload: async (file) => {
      setUploading(true)
      try {
        const res = await uploadKycDocument(file)
        if (!active.current) return false
        onUploaded(fieldKey, res.data.url)
        setFileList([{ uid: file.uid, name: file.name, status: 'done' }])
        message.success(`${label} 上傳成功`)
      } catch (error: unknown) {
        if (!active.current) return false
        const detail = error instanceof Error ? error.message : '請稍後重試'
        message.error(`${label} 上傳失敗：${detail}`)
        setFileList([])
      } finally {
        if (active.current) setUploading(false)
      }
      return false
    },
    onRemove: () => {
      setFileList([])
      onUploaded(fieldKey, '')
    },
  }

  return (
    <Form.Item
      label={
        <span>
          {required && <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>}
          {label}
        </span>
      }
      style={{ marginBottom: 16 }}
    >
      {uploaded ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: 6,
        }}>
          <CheckCircleFilled style={{ color: '#22c55e' }} />
          <Text style={{ color: 'var(--landing-text-primary)', flex: 1, fontSize: 13 }}>
            {fileList[0]?.name || '已上傳'}
          </Text>
          <Button
            type="text"
            size="small"
            style={{ color: 'var(--landing-text-muted)' }}
            onClick={() => {
              setFileList([])
              onUploaded(fieldKey, '')
            }}
          >
            重新上傳
          </Button>
        </div>
      ) : (
        <Dragger {...props} style={{ background: 'rgba(15, 23, 42, 0.4)', borderColor: 'rgba(99, 102, 241, 0.3)' }}>
          <p className="ant-upload-drag-icon">
            {uploading ? <LoadingOutlined style={{ color: 'var(--landing-accent-cyan)' }} /> : <InboxOutlined style={{ color: 'var(--landing-accent-cyan)' }} />}
          </p>
          <p style={{ color: 'var(--landing-text-secondary)', margin: 0, fontSize: 13 }}>
            {uploading ? '上傳中...' : '點擊或拖曳檔案到此區域上傳'}
          </p>
          <p style={{ color: 'var(--landing-text-muted)', margin: '4px 0 0', fontSize: 12 }}>
            支援 JPG、PNG、PDF，單檔案不超過 10MB
          </p>
        </Dragger>
      )}
    </Form.Item>
  )
}
